import { CHANNEL_INFO, ORDER_STATUS_LABELS, PERSONAL_DATA_ERASED, type OrderStatus } from '@wms/domain';

/** Times are shown in Türkiye's time, as the marketplaces show them, whatever a device's own clock says. */
export const DEFAULT_TIME_ZONE = 'Europe/Istanbul';
/** "device": this device's own zone; an IANA name ("Europe/Berlin"); absent: Türkiye */
export const TIME_ZONE_KEY = 'wms.timeZone';

const deviceZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;
const zoneOf = (choice: string | null) => {
  if (choice === 'device') return deviceZone();
  try {
    if (choice) new Intl.DateTimeFormat('tr-TR', { timeZone: choice });
    return choice || DEFAULT_TIME_ZONE;
  } catch {
    return DEFAULT_TIME_ZONE;
  }
};

let zone = DEFAULT_TIME_ZONE;
try {
  zone = zoneOf(localStorage.getItem(TIME_ZONE_KEY));
} catch {
  // storage blocked: Türkiye
}

const number = new Intl.NumberFormat('tr-TR');
const decimal = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 2 });
const money = new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' });
// a day without a time ("2026-10-17") is the same day everywhere: drawn in UTC, never shifted
const day = new Intl.DateTimeFormat('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' });
const monthName = new Intl.DateTimeFormat('tr-TR', { month: 'long', year: 'numeric', timeZone: 'UTC' });
let dateTime = new Intl.DateTimeFormat('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: zone });
let stamp = new Intl.DateTimeFormat('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: zone });
let date = new Intl.DateTimeFormat('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: zone });
let isoDay = new Intl.DateTimeFormat('en-CA', { timeZone: zone });

/** The zone times are shown in now. */
export const timeZone = () => zone;

/** Shows times in another zone from now on ("device", an IANA name, or null for Türkiye) and keeps the choice on this device. */
export function setTimeZone(choice: string | null): void {
  zone = zoneOf(choice);
  dateTime = new Intl.DateTimeFormat('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: zone });
  stamp = new Intl.DateTimeFormat('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: zone });
  date = new Intl.DateTimeFormat('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: zone });
  isoDay = new Intl.DateTimeFormat('en-CA', { timeZone: zone });
  try {
    if (choice) localStorage.setItem(TIME_ZONE_KEY, choice);
    else localStorage.removeItem(TIME_ZONE_KEY);
  } catch {
    // not kept: this visit only
  }
}

export const fmt = {
  n: (value: number) => number.format(value),
  dec: (value: number) => decimal.format(value),
  dateTime: (iso: string) => dateTime.format(new Date(iso)),
  /** with the year: "24.10.2026 21:30" */
  stamp: (iso: string) => stamp.format(new Date(iso)),
  date: (iso: string) => (iso.length === 10 ? day.format(new Date(`${iso}T00:00:00Z`)) : date.format(new Date(iso))),
  /** today in the shown zone, "2026-10-17" */
  today: () => isoDay.format(new Date()),
  /** a moment's day in the shown zone, "2026-10-17" */
  isoDay: (moment: Date) => isoDay.format(moment),
  /** decimal string from the API ("123.45") as "₺123,45" */
  money: (value: string | number) => money.format(Number(value)),
  /** "Eylül 2026" */
  month: (isoDay: string) => monthName.format(new Date(`${isoDay.slice(0, 7)}-01T00:00:00Z`)),
  /** "12 dk", "3 sa", "2 gün" */
  age(iso: string): string {
    const minutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
    if (minutes < 60) return `${minutes} dk`;
    if (minutes < 48 * 60) return `${Math.round(minutes / 60)} sa`;
    return `${Math.round(minutes / 1440)} gün`;
  },
  /** Days until an expiry date (negative = expired) */
  daysLeft(isoDay: string): number {
    const end = new Date(`${isoDay}T00:00:00`).getTime();
    const today = new Date(new Date().toDateString()).getTime();
    return Math.round((end - today) / 86_400_000);
  },
};

export const statusLabel = (status: OrderStatus) => ORDER_STATUS_LABELS[status];

const CARRIERS: Readonly<Record<string, string>> = {
  aras: 'Aras Kargo',
  yurtici: 'Yurtiçi Kargo',
  mng: 'MNG Kargo',
  surat: 'Sürat Kargo',
  ptt: 'PTT Kargo',
  hepsijet: 'HepsiJet',
  trendyol_express: 'Trendyol Express',
  kolay_gelsin: 'Kolay Gelsin',
  horoz: 'Horoz Lojistik',
  ceva: 'Ceva Lojistik',
  borusan: 'Borusan Lojistik',
  sendeo: 'Sendeo',
  kargoist: 'Kargoist',
  ups: 'UPS',
  amazon_shipping: 'Amazon Shipping',
};
/**
 * The marketplace an order came from, as screens name it: in full where there is room ("Trendyol"), by its
 * short code where room is tight ("TY"). A channel the list does not know reads as itself in both.
 */
export function channelText(channel: string): { readonly name: string; readonly short: string } {
  if (!Object.hasOwn(CHANNEL_INFO, channel)) return { name: channel, short: channel };
  const { name, short } = CHANNEL_INFO[channel as keyof typeof CHANNEL_INFO];
  return { name, short };
}

/** "aras" → "Aras Kargo"; a code kept from an unlisted carrier's name reads as that name. */
export const carrierLabel = (code: string | null) =>
  code ? (CARRIERS[code] ?? code.split('_').map((w) => w.charAt(0).toLocaleUpperCase('tr-TR') + w.slice(1)).join(' ')) : 'Tüm kargo firmaları';

/** "12,50" or "12.5" as typed → "12.50"-style decimal for the API (null when not a number). */
export function decimalInput(value: string, digits = 2): string | null {
  const v = value.trim().replace(/\s/g, '').replace(',', '.');
  if (!/^\d+(\.\d+)?$/.test(v)) return null;
  const [whole, fraction = ''] = v.split('.');
  return fraction ? `${whole}.${fraction.slice(0, digits)}` : whole!;
}

export function statusTone(status: OrderStatus): 'ok' | 'warn' | 'bad' | 'info' | '' {
  switch (status) {
    case 'allocated':
    case 'picked':
    case 'packed':
      return 'info';
    case 'shipped':
    case 'delivered':
      return 'ok';
    case 'backordered':
    case 'pending':
      return 'warn';
    case 'on_hold':
    case 'cancelled':
    case 'returned':
      return 'bad';
    default:
      return '';
  }
}

/** Bin label barcode (LB030201) as the code people read (B-03-2-1); other input unchanged */
export function binCode(scanned: string): string {
  const m = /^L([A-Z]{1,3})(\d{2})(\d{2})(\d{2})$/.exec(scanned);
  return m ? `${m[1]}-${m[2]}-${Number(m[3])}-${Number(m[4])}` : scanned;
}

/** Stable colour per product for the thumbnail when it has no image */
export function swatch(text: string): string {
  let hash = 0;
  for (const ch of text) hash = (hash * 31 + ch.charCodeAt(0)) | 0;
  return `hsl(${Math.abs(hash) % 360} 52% 40%)`;
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter((word) => /\p{L}/u.test(word[0] ?? ''))
    .slice(0, 2)
    .map((word) => word[0]!.toLocaleUpperCase('tr-TR'))
    .join('');
}

/**
 * Who an order is for, as lists show it: the buyer's name; once it was erased (KVKK), that it was
 * and when ("Kişisel veriler silindi (23.10.2026)"); otherwise the fallback.
 */
export function customerText(o: { readonly customerName: string | null; readonly personalDataErasedAt?: string | null }, fallback = '—'): string {
  if (o.customerName) return o.customerName;
  if (o.personalDataErasedAt) return `${PERSONAL_DATA_ERASED} (${fmt.date(o.personalDataErasedAt)})`;
  return fallback;
}
